import {
  difference,
  drop,
  indexOf,
  map,
  orderBy,
  remove,
  sampleSize,
  uniq,
  times,
} from 'lodash';
import { isAfter } from 'date-fns';
import React, {
  useCallback,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';

import { useInterval } from '../hooks/use_interval';

// import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Box, PageWrapper, PopupWrapper } from '../styles';

import Entries from '../pages/entries';
import Promo from '../pages/promo';
import Result from '../pages/result';
import Winners from '../pages/winners';

function ScoredCarousel({
  animationEndedCallback,
  calculatedQuestions,
  calculatedQuestionsWithWinners,
  promoPages,
  questions,
  userEntries,
  promoPageTimer,
  entriesPageTimer,
  resultsPageTimer,
  winnerPageTimer,
  randomiseResultsPages,
  randomiseResultsPagesCount,
  resultsPagesExcluded,
}) {
  const [pages, setPages] = useState([]);
  const [timer, setTimer] = useState(promoPageTimer);
  const [currentPage, setCurrentPage] = useState(4);
  const [showResultsPage, setShowResultsPage] = useState(false);
  const [resultsPagesToShow, setResultsPagesToShow] = useState([]);

  const calculatedQuestionsRef = useRef(calculatedQuestions);
  const [promoPageCount, setPromoPageCount] = useState();
  const [resultsPageCount, setResultsPageCount] = useState();

  // Back and forward keyboard events
  useEffect(() => {
    const listener = event => {
      switch (event.key) {
        case 'ArrowLeft':
          if (currentPage > 1) {
            setCurrentPage(current => current - 1);
          } else {
            setCurrentPage(pages.length);
          }
          break;
        case 'ArrowRight':
          if (currentPage < pages.length) {
            setCurrentPage(current => current + 1);
          } else {
            setCurrentPage(pages.length);
          }
          break;
      }
    };

    document.addEventListener('keyup', listener);

    return () => {
      document.removeEventListener('keyup', listener);
    };
  });

  // Rank entries
  useEffect(() => {
    const uniqScores = orderBy(
      uniq(map(userEntries, (entry, id) => userEntries[id].score)),
      [],
      'desc'
    );

    const sorted = orderBy(
      map(userEntries, (entry, id) => id),
      id => userEntries[id].score,
      'desc'
    );

    sorted.map(id => {
      userEntries[id].rank = indexOf(uniqScores, userEntries[id].score) + 1;
    });
  }, [userEntries]);

  // Build promo pages
  const promoPagesPages = useMemo(() => {
    const now = Date.now();

    promoPages.sort((first, second) =>
      isAfter(first.attributes.created_at, second.attributes.created_at)
        ? 1
        : -1
    );

    return promoPages
      .map(({ id, attributes }) => ({
        key: id,
        type: 'promo',
        data: attributes,
      }))
      .filter(({ data: { expires_at = null } }) => {
        if (expires_at) {
          return isAfter(expires_at, now);
        } else {
          return true;
        }
      });
  }, [promoPages]);

  // Build entry pages
  const entryPages = useMemo(
    () =>
      times(
        Math.ceil(Object.keys(userEntries).length / 9),

        () => ({
          type: 'entries',
        })
      ),
    [userEntries]
  );

  // Build previous results pages
  const resultsPages = useMemo(() => {
    let cqww = [...calculatedQuestionsWithWinners];

    if (
      Array.isArray(resultsPagesExcluded) &&
      resultsPagesExcluded.length > 0
    ) {
      cqww = remove(cqww, id => resultsPagesExcluded.includes(id));
    }

    if (randomiseResultsPages) {
      cqww = sampleSize(cqww, randomiseResultsPagesCount);
    }

    return cqww.map(question => ({
      key: question,
      type: 'results',
    }));
  }, [
    calculatedQuestionsWithWinners,
    resultsPagesExcluded,
    randomiseResultsPages,
    randomiseResultsPagesCount,
  ]);

  // Build winner page
  const winnerPage = useMemo(() => {
    if (calculatedQuestions.length === Object.keys(questions).length) {
      return [
        {
          key: 'winners',
          type: 'winners',
        },
      ];
    } else {
      return [];
    }
  }, [calculatedQuestions.length, questions]);

  // Concat all pages
  const buildPages = useCallback(() => {
    setPages([
      ...promoPagesPages,
      ...entryPages,
      ...resultsPages,
      ...winnerPage,
    ]);

    setPromoPageCount(promoPagesPages.length);
    setResultsPageCount(resultsPages.length);
  }, [promoPagesPages, entryPages, resultsPages, winnerPage]);

  useLayoutEffect(buildPages, [buildPages]);

  // Add new results to page overlay stack
  useCallback(() => {
    const newQuestions = difference(
      calculatedQuestions,
      calculatedQuestionsRef.current
    );

    setResultsPagesToShow([...resultsPagesToShow, ...newQuestions]);
  }, [calculatedQuestions, calculatedQuestionsRef, resultsPagesToShow]);

  const displayedPage = useMemo(
    () => pages[currentPage - 1],
    [pages, currentPage]
  );

  // Use specific timers for page types
  useEffect(() => {
    if (displayedPage && displayedPage.type) {
      switch (displayedPage.type) {
        case 'promo':
          setTimer(promoPageTimer);
          break;
        case 'results':
          setTimer(resultsPageTimer);
          break;
        case 'winners':
          setTimer(winnerPageTimer);
          break;
        default:
          setTimer(entriesPageTimer);
      }
    }
  }, [
    displayedPage,
    entriesPageTimer,
    promoPageTimer,
    resultsPageTimer,
    winnerPageTimer,
  ]);

  // Page change timer
  useInterval(() => {
    if (currentPage < pages.length) {
      setCurrentPage(current => current + 1);
    } else {
      setCurrentPage(1);
      animationEndedCallback();
    }
  }, timer);

  // Count down new results pages and drop from stack
  useInterval(
    () => {
      if (resultsPageCount > 1) {
        setResultsPagesToShow(drop(resultsPagesToShow));
      } else {
        setShowResultsPage(false);
      }
    },
    showResultsPage ? resultsPageTimer : null
  );

  // Trigger overlays
  useCallback(() => {
    if (resultsPageCount > 1) {
      setShowResultsPage(true);
    }
  }, [resultsPageCount]);

  return (
    <Box>
      {pages && pages.length > 0 && displayedPage ? (
        displayedPage.type === 'promo' ? (
          <PageWrapper key={displayedPage.key}>
            <Promo key={displayedPage.key} data={displayedPage.data} />
          </PageWrapper>
        ) : displayedPage.type === 'results' ? (
          <PageWrapper key={displayedPage.key}>
            <Result
              userEntries={userEntries}
              questionId={displayedPage.key}
              question={questions[displayedPage.key]}
            />
          </PageWrapper>
        ) : displayedPage.type === 'entries' ? (
          <PageWrapper key={'entries-page'}>
            <Entries
              key="entry-page"
              userEntries={userEntries}
              questions={questions}
              offset={promoPageCount + 1}
              timer={currentPage}
            />
          </PageWrapper>
        ) : displayedPage.type === 'winners' ? (
          <PageWrapper key={'winners-page'}>
            <Winners key={displayedPage.key} userEntries={userEntries} />
          </PageWrapper>
        ) : null
      ) : null}

      {showResultsPage ? (
        <PopupWrapper key={`results-page-${resultsPagesToShow[0]}`}>
          <Result
            userEntries={userEntries}
            questionId={resultsPagesToShow[0]}
            question={questions[resultsPagesToShow[0]]}
          />
        </PopupWrapper>
      ) : null}
    </Box>
  );
}

export default ScoredCarousel;
